import CommonConstants from '@/constants/common-constants';
import { getProtectedAxiosInstance } from '@/services/axios/Axios';
import {
  buildPhotoCheckForm,
  PreparedMedia,
  prepareMedia
} from '@/services/photo-check/photo-check.service';
import { photoCriteria } from '@/services/photo-check/photo-check-tasks';
import { notDoneLabel } from '@/services/photo-check/problem-label';
import { pickPhoto } from '@/services/photo-check/merge-photos';
import { getRunTaskStatus } from '@/services/photo-check/run-task-status-registry';
import { usePublicRunStore } from '@/stores/public-run/public-run-checklist.store';
import { useRunChecklistStore } from '@/stores/run/run-checklist.store';
import { useRunPhotoSessionStore } from '@/stores/run/run-photo-session.store';
import PhotoCloseButton from './photo-close-button.component';
import PhotoGeofenceBadge from './photo-geofence-badge.component';
import {
  PhotoLocation,
  PhotoCheckConfig,
  PhotoCheckResponse,
  PhotoCheckTaskResult
} from '@/types/photo-check.type';
import { Dialog, Transition } from '@headlessui/react';
import axios from 'axios';
import { Fragment, useEffect, useState } from 'react';
import { shallow } from 'zustand/shallow';
import { RunChecklistTree } from '../../run-by-id.component';
import RunPhotoCheckCamera, {
  formatPhotoTime
} from './run-photo-check-camera.component';

type Decision = 'done' | 'not_done' | null;

const TAKE_PHOTO_BUTTON = 'photo-check-take-photo';

// A person can take several photos (one per area: desk, bin, floor...) and submit them together
const MAX_SHOTS = 10;

type Shot = {
  file: File;
  takenAt: Date;
  location?: PhotoLocation;
  url: string;
};
type CheckedShot = {
  shot: Shot;
  prepared: PreparedMedia;
  response: PhotoCheckResponse;
};

/**
 * Every photo is checked against every task. For each task, use the photo that shows it (see pickPhoto, the
 * same rule Analytics uses). Seen done in one photo and something wrong in another: the newest photo decides.
 * A task in no photo is left as it is.
 */
function mergeShots(
  shots: CheckedShot[],
  checks: { task: RunChecklistTree; check: PhotoCheckConfig }[]
) {
  const results: PhotoCheckTaskResult[] = [];
  const decidedBy: { [taskId: string]: number } = {};
  const outOfPhoto: string[] = [];
  for (const { task, check } of checks) {
    const taskId = task.checklist.task_id as string;
    const answers = shots.flatMap((s, index) => {
      const answer = s.response.results.find(x => x.task_id === taskId);
      return answer ? [{ answer, index }] : [];
    });
    const pick = pickPhoto(answers, check as any);
    if (!pick) {
      outOfPhoto.push(taskId);
      continue;
    }
    // Photos that disagree: the newest one decides (see pickPhoto)
    results.push(pick.answer);
    decidedBy[taskId] = pick.index;
  }
  return { results, decidedBy, outOfPhoto };
}

/**
 * Check one section of a run from a photo or short video. Tasks Jev is sure are done are ticked straight away
 * (with Undo); tasks seen not done get the checklist's "Incomplete" column when it has one (otherwise stay open);
 * the person only decides the ones Jev is unsure about.
 */
export default function RunPhotoCheckPanel({
  section,
  checks,
  onClose
}: {
  section: RunChecklistTree;
  // The tasks this photo checks, with how each is checked (see photoCheckTasks)
  checks: { task: RunChecklistTree; check: PhotoCheckConfig }[];
  onClose: () => void;
}) {
  const [isPublic, isPreview, refreshRunChecklistTree, runChecklistTree] =
    useRunChecklistStore(
      s => [
        s.isPublic,
        s.isPreview,
        s.refreshRunChecklistTree,
        s.runChecklistTree
      ],
      shallow
    );
  const [publicRunAccessToken] = usePublicRunStore(
    s => [s.publicRunAccessToken],
    shallow
  );

  // Photos taken so far, checked together on Submit
  const [shots, setShots] = useState<Shot[]>([]);
  // Each photo with what Jev said about it, in the order taken
  // Earlier photos of this run (popup closed and opened again): start on their results
  const runKey = String(
    section.checklist.run_id || section.checklist.id || 'run'
  );
  const [saved] = useState(
    () => useRunPhotoSessionStore.getState().sessions[runKey]
  );
  const [checked, setChecked] = useState<CheckedShot[]>(saved?.checked || []);
  // The photo shown large on the results screen, and which photo decided each task
  const [shown, setShown] = useState(0);
  const [decidedBy, setDecidedBy] = useState<{ [taskId: string]: number }>(
    saved?.decidedBy || {}
  );
  // Asking "Remove all photos and undo what AI ticked?" in the footer
  const [confirmStartOver, setConfirmStartOver] = useState(false);
  // "Turn on" next to "No location": shows how to allow location
  const [showLocationHelp, setShowLocationHelp] = useState(false);
  // The square next to the photos is a live camera: tap it to take another photo without leaving the screen
  const [tileCamera, setTileCamera] = useState(false);
  // "How it works" on phones: folded until tapped
  const [showHowItWorks, setShowHowItWorks] = useState(false);
  // The "tasks not in the photo" list on the results screen is folded until opened
  const [showMissing, setShowMissing] = useState(false);
  // camera: live camera (opens straight away) → ready: photos taken, not checked yet → working → results
  const [step, setStep] = useState<'camera' | 'ready' | 'working' | 'results'>(
    saved?.checked?.length ? 'results' : 'camera'
  );
  const [progress, setProgress] = useState('');
  const [error, setError] = useState('');
  // Tasks this photo did not show: left as they were, listed only as a count
  const [outOfPhoto, setOutOfPhoto] = useState<string[]>(
    saved?.outOfPhoto || []
  );
  const [response, setResponse] = useState<PhotoCheckResponse | undefined>(
    saved?.response
  );
  // What the panel has set on each task: done, left open, or not yet decided (Jev unsure)
  // Reopened: show each task as it is now on the run (the person may have changed it since)
  const [decisions, setDecisions] = useState<{ [taskId: string]: Decision }>(
    () => {
      const now: { [taskId: string]: Decision } = {};
      (saved?.response?.results || []).forEach((r: PhotoCheckTaskResult) => {
        const handle = getRunTaskStatus(r.task_id);
        now[r.task_id] = handle?.isDone()
          ? 'done'
          : handle?.isNotDone()
          ? 'not_done'
          : null;
      });
      return now;
    }
  );
  const [applying, setApplying] = useState(false);
  // Tasks the person may not change now (not assigned to them, or waiting for an earlier step)
  const [blocked, setBlocked] = useState<string[]>([]);

  // When the camera opens (e.g. "Take another photo" at the bottom of a phone screen), bring its Take photo
  // button into view. Again a moment later, once the camera picture has its full height.
  useEffect(() => {
    if (!(step === 'camera' || tileCamera)) return;
    const show = () => {
      const button = document.getElementById(TAKE_PHOTO_BUTTON);
      button?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      button?.focus({ preventScroll: true });
    };
    const timers = [100, 900].map(ms => setTimeout(show, ms));
    return () => timers.forEach(clearTimeout);
  }, [step, tileCamera]);

  // Keep this run's photos and results for the next time the popup opens
  useEffect(() => {
    const store = useRunPhotoSessionStore.getState();
    if (checked.length) {
      store.save(runKey, { checked, decidedBy, outOfPhoto, response });
    } else {
      store.clear(runKey);
    }
  }, [checked, decidedBy, outOfPhoto, response]);

  const area = (section.checklist?.photo_check as any)?.area;
  // Set by the creator on the checklist title; the same for every task's camera
  const criteria = photoCriteria(runChecklistTree);
  // The original video goes straight to Cloudflare R2 with a signed link from our backend. Photos and frames
  // are saved by the backend from what it sends Jev, so they need no upload here. Returns the R2 key, or nothing.
  async function uploadVideo(file: File): Promise<string | undefined> {
    if (isPreview || !file.type.startsWith('video/')) return undefined;
    const body = {
      run_id: isPublic ? undefined : section.checklist.run_id,
      content_type: file.type,
      size: file.size
    };
    const link = isPublic
      ? await axios.post(
          CommonConstants.API_PREFIX + '/public/run/photo-check/video-upload',
          body,
          { headers: { Authorization: 'Bearer ' + publicRunAccessToken } }
        )
      : await getProtectedAxiosInstance().post(
          '/run/photo-check/video-upload',
          body
        );
    const { key, url } = link.data.data;
    const put = await fetch(url, {
      method: 'PUT',
      headers: { 'Content-Type': file.type },
      body: file
    });
    return put.ok ? key : undefined;
  }

  // Only photos taken with the in-page camera reach here (no uploads), so they cannot be old or copied
  function addShot(file: File, taken: Date, location?: PhotoLocation) {
    setError('');
    // Keep the camera open so several photos can be taken in a row; each one lands in the row below
    // and the footer offers "Check N photos"
    setTileCamera(true);
    setStep('ready');
    setShots(list =>
      list.length + checked.length >= MAX_SHOTS
        ? list
        : [
            ...list,
            { file, takenAt: taken, location, url: URL.createObjectURL(file) }
          ]
    );
  }

  function removeShot(index: number) {
    setShots(list => {
      URL.revokeObjectURL(list[index]?.url);
      return list.filter((_, i) => i !== index);
    });
  }

  // Send one photo to be checked against every task
  async function checkShot(shot: Shot): Promise<CheckedShot> {
    const videoUpload = uploadVideo(shot.file).catch(() => undefined);
    const media = await prepareMedia(shot.file);
    const videoKey = await videoUpload;
    const form = buildPhotoCheckForm(media, {
      area,
      tasks: checks.map(({ task, check }) => ({
        id: task.checklist.task_id as string,
        title: task.checklist.title,
        photo_check: check
      })),
      run_id: isPublic || isPreview ? undefined : section.checklist.run_id,
      // Builder preview has no run: the server checks the tasks against this checklist instead
      checklist_id: isPreview
        ? (runChecklistTree?.checklist.task_id as string)
        : undefined,
      section_id: section.checklist.id,
      video_key: videoKey,
      taken_at: shot.takenAt.toISOString(),
      location: shot.location
    });
    const result = isPublic
      ? await axios.post(
          CommonConstants.API_PREFIX + '/public/run/photo-check',
          form,
          { headers: { Authorization: 'Bearer ' + publicRunAccessToken } }
        )
      : await getProtectedAxiosInstance().post('/run/photo-check', form);
    return { shot, prepared: media, response: result.data.data };
  }

  // Submit: check every photo, then for each task use the photo that shows it best, and mark the checklist
  // Check new photos and add them to the ones already checked. The first time every task is marked; when more
  // photos are added from the results screen, only tasks a new photo now decides are changed, so earlier
  // ticks and the person's own answers stay.
  async function checkAndApply(newShots: Shot[], previous: CheckedShot[]) {
    if (!newShots.length) return;
    const total = previous.length + newShots.length;
    setError('');
    setStep('working');
    try {
      // Check the photos at the same time (a few at once), not one after another
      let finished = 0;
      setProgress(
        newShots.length > 1
          ? `Checking ${newShots.length} photos…`
          : 'Checking the photo…'
      );
      const added: CheckedShot[] = new Array(newShots.length);
      const queue = newShots.map((shot, i) => ({ shot, i }));
      const worker = async () => {
        for (let next = queue.shift(); next; next = queue.shift()) {
          added[next.i] = await checkShot(next.shot);
          finished++;
          if (newShots.length > 1)
            setProgress(`Checked ${finished} of ${newShots.length} photos…`);
        }
      };
      await Promise.all(
        Array.from({ length: Math.min(4, newShots.length) }, worker)
      );
      const all = [...previous, ...added];
      setChecked(all);
      // The shots now live in "checked"; the camera strip starts empty for the next ones
      setShots([]);

      const merged = mergeShots(all, checks);
      setDecidedBy(merged.decidedBy);
      setOutOfPhoto(merged.outOfPhoto);
      setShown(previous.length);
      const data: PhotoCheckResponse = {
        media: 'photo',
        ms: 0,
        results: merged.results,
        geofence: all[0]?.response.geofence
      };
      setResponse(data);
      setStep('results');
      setTileCamera(false);

      // Done ticks "Completed", not done ticks "Incomplete" when the checklist has that column (otherwise the
      // task stays open). Unsure ones wait for the person. Tasks in no photo are left exactly as they are.
      const fresh = merged.results.filter(
        r => merged.decidedBy[r.task_id] >= previous.length
      );
      setApplying(true);
      setDecisions(current => {
        const next = previous.length ? { ...current } : {};
        for (const r of fresh) {
          if (r.suggestion === 'review') next[r.task_id] = null;
          else delete next[r.task_id];
        }
        return next;
      });
      for (const r of fresh) {
        if (r.suggestion === 'done') {
          const decision = (await setTaskDone(r.task_id, true)) ? 'done' : null;
          setDecisions(current => ({ ...current, [r.task_id]: decision }));
        } else if (r.suggestion === 'attention') {
          await setTaskNotDone(r.task_id);
          setDecisions(current => ({ ...current, [r.task_id]: 'not_done' }));
        }
      }
      setApplying(false);
    } catch (e: any) {
      setError(
        e?.response?.data?.error?.message ||
          e?.message ||
          'Something went wrong. Please try again.'
      );
      setApplying(false);
      setStep('ready');
    }
  }

  const checkPhotos = () => checkAndApply(shots, checked);

  function discardShots() {
    shots.forEach(shot => URL.revokeObjectURL(shot.url));
    setShots([]);
  }

  // Cancel on the camera: back to the photos taken so far, or close when there are none
  function cancelCamera() {
    setError('');
    if (shots.length) setStep('ready');
    else if (checked.length) setStep('results');
    else onClose();
  }

  // Cancel on "Ready to check": drop the photos not checked yet
  function cancelReady() {
    discardShots();
    setTileCamera(false);
    setError('');
    if (checked.length) setStep('results');
    else onClose();
  }

  // Start over: remove every photo and undo what was ticked from them, then open the camera again
  async function startOver() {
    setConfirmStartOver(false);
    setApplying(true);
    for (const [taskId, decision] of Object.entries(decisions)) {
      const handle = getRunTaskStatus(taskId);
      if (!handle || isPreview || !handle.canChange()) continue;
      if (decision === 'done' && handle.isDone()) await handle.setDone(false);
      if (decision === 'not_done' && handle.isNotDone())
        await handle.clearNotDone?.();
    }
    refreshRunChecklistTree();
    setApplying(false);
    discardShots();
    checked.forEach(c => URL.revokeObjectURL(c.shot.url));
    setChecked([]);
    setResponse(undefined);
    setOutOfPhoto([]);
    setDecidedBy({});
    setDecisions({});
    setBlocked([]);
    setError('');
    setShowMissing(false);
    setTileCamera(false);
    setStep('camera');
  }

  // Set one task through the same path as its checkbox (permissions, workflow order, notifications).
  // Returns false when the person may not change it now.
  async function setTaskDone(taskId: string, done: boolean) {
    const handle = getRunTaskStatus(taskId);
    if (!handle) return false;
    if (handle.isDone() !== done) {
      if (isPreview || !handle.canChange()) {
        setBlocked(list => (list.includes(taskId) ? list : [...list, taskId]));
        return false;
      }
      await handle.setDone(done);
      refreshRunChecklistTree();
    }
    setBlocked(list => list.filter(id => id !== taskId));
    return true;
  }

  // Not done: tick the "Incomplete" column when the checklist has one, otherwise leave the task unticked.
  // Returns false when the person may not change it now.
  async function setTaskNotDone(taskId: string) {
    const handle = getRunTaskStatus(taskId);
    if (!handle) return false;
    if (!handle.isNotDone()) {
      if (isPreview || !handle.canChange()) {
        setBlocked(list => (list.includes(taskId) ? list : [...list, taskId]));
        return false;
      }
      if (!(await handle.setNotDone()) && handle.isDone()) {
        await handle.setDone(false);
      }
      refreshRunChecklistTree();
    }
    setBlocked(list => list.filter(id => id !== taskId));
    return true;
  }

  // Undo a tick: clear it and ask again, rather than marking the task not done
  async function undoDone(taskId: string) {
    setApplying(true);
    if (await setTaskDone(taskId, false)) {
      setDecisions(current => ({ ...current, [taskId]: null }));
    }
    setApplying(false);
  }

  async function decide(taskId: string, decision: 'done' | 'not_done') {
    setApplying(true);
    const applied =
      decision === 'done'
        ? await setTaskDone(taskId, true)
        : await setTaskNotDone(taskId);
    if (applied) {
      setDecisions(current => ({ ...current, [taskId]: decision }));
    }
    setApplying(false);
  }

  const ticked = Object.values(decisions).filter(d => d === 'done').length;
  const toCheck = Object.values(decisions).filter(d => d === null).length;
  const results = response?.results || [];

  // The big photo: the newest one not checked yet on "Ready to check", otherwise the one picked below it
  const pending = step === 'ready' && shots.length > 0;
  const bigUrl = pending
    ? shots[shots.length - 1].url
    : checked[shown]?.prepared?.views[0]?.url || checked[shown]?.shot.url;
  const bigShot = pending ? shots[shots.length - 1] : checked[shown]?.shot;
  const room = MAX_SHOTS - checked.length - shots.length;

  const CameraIcon = ({ size = 18 }: { size?: number }) => (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M3 8a2 2 0 0 1 2-2h2l1.5-2h7L17 6h2a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
      <circle cx="12" cy="13" r="3.5" />
    </svg>
  );

  const pillButton =
    'px-5 py-2.5 rounded-full border border-gray-700 text-sm font-medium disabled:opacity-40';
  const primaryButton = pillButton + ' bg-[#ffa726] hover:brightness-95';
  const secondaryButton = pillButton + ' bg-white hover:bg-slate-100';
  const linkButton =
    'text-sm font-medium underline underline-offset-2 disabled:opacity-40';

  // On phones the steps and examples fold under the title (closed at first); on wider screens always open
  const howItWorks = (title: string) => (
    <div className="flex flex-col gap-4">
      <button
        type="button"
        className="flex items-center justify-between gap-2 text-left md:cursor-default"
        onClick={() => setShowHowItWorks(v => !v)}
        aria-expanded={showHowItWorks}
      >
        <h3 className="text-xl font-semibold">{title}</h3>
        <svg
          className={
            'shrink-0 md:hidden transition-transform ' +
            (showHowItWorks ? 'rotate-180' : '')
          }
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M6 9l6 6 6-6" />
        </svg>
      </button>
      <div
        className={
          (showHowItWorks ? 'flex' : 'hidden') + ' md:flex flex-col gap-4'
        }
      >
        <ol className="flex flex-col gap-3">
          {[
            'Take one or more photos of the finished work.',
            'AI ticks the tasks it can see. Tasks not in a photo stay as they are.',
            'Check the results. You can undo anything it ticks.'
          ].map((text, i) => (
            <li
              key={i}
              className="flex items-start gap-3 text-sm text-black/80"
            >
              <span className="shrink-0 h-6 w-6 rounded-full bg-black/5 text-xs font-semibold flex items-center justify-center">
                {i + 1}
              </span>
              <span className="pt-0.5">{text}</span>
            </li>
          ))}
        </ol>
        {(criteria.looks_like || criteria.references) && (
          <div className="rounded-xl bg-black/[0.03] p-3 flex flex-col gap-2">
            <div className="text-xs font-medium text-black/70">
              How it should look
            </div>
            {criteria.looks_like && (
              <p className="text-sm text-black/80 whitespace-pre-line">
                {criteria.looks_like}
              </p>
            )}
            {criteria.references && (
              <div className="flex gap-2 overflow-x-auto">
                {criteria.references.map((ref, i) => (
                  <a
                    key={ref.file_id}
                    href={ref.url}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <img
                      src={ref.url}
                      alt={`Example ${i + 1}`}
                      className="h-16 w-16 object-cover rounded-lg border border-black/10"
                    />
                  </a>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );

  // Time and place under the big photo
  const photoMeta = bigShot && (
    <div className="flex flex-col gap-1">
      <div className="flex flex-wrap items-center gap-x-5 gap-y-1 text-sm text-black/60">
        <span className="flex items-center gap-1.5">
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            aria-hidden="true"
          >
            <circle cx="12" cy="12" r="9" />
            <path d="M12 7v5l3 2" />
          </svg>
          {formatPhotoTime(bigShot.takenAt)}
        </span>
        <span className="flex items-center gap-1.5">
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M12 21s-7-6.2-7-11.5a7 7 0 0 1 14 0C19 14.8 12 21 12 21z" />
            <circle cx="12" cy="9.5" r="2.5" />
          </svg>
          {bigShot.location ? (
            <>
              Within {bigShot.location.accuracy} m ·{' '}
              <a
                href={`https://www.google.com/maps?q=${bigShot.location.lat},${bigShot.location.lng}`}
                target="_blank"
                rel="noreferrer"
                className="underline underline-offset-2 text-black"
              >
                Map
              </a>
            </>
          ) : (
            <>
              No location{' '}
              <button
                type="button"
                className="underline underline-offset-2 text-black font-medium"
                onClick={() => setShowLocationHelp(v => !v)}
              >
                Turn on
              </button>
            </>
          )}
        </span>
      </div>
      {!bigShot.location && showLocationHelp && (
        <p className="text-xs text-black/60">
          Allow location for this site in your browser settings (the lock icon
          next to the address), then take the photo again.
        </p>
      )}
      {!pending && checked[shown]?.response.geofence && (
        <PhotoGeofenceBadge
          geofence={checked[shown].response.geofence}
          location={bigShot.location}
        />
      )}
    </div>
  );

  // Photos: checked ones (tap to view), new ones with ✕, and a tile to take another
  const thumbnails = (
    <div className="flex gap-3 overflow-x-auto pt-2 pr-2">
      {checked.map((c, i) => (
        <button
          key={c.shot.url}
          type="button"
          aria-label={`Show photo ${i + 1}`}
          onClick={() => {
            setShown(i);
            setTileCamera(false);
          }}
          className={
            'shrink-0 rounded-xl border-2 overflow-hidden ' +
            (!pending && i === shown ? 'border-black' : 'border-black/10')
          }
        >
          <img
            src={c.shot.url}
            alt={`Photo ${i + 1}`}
            className="h-16 w-20 object-cover"
          />
        </button>
      ))}
      {step === 'ready' &&
        shots.map((shot, i) => (
          <div key={shot.url} className="relative shrink-0">
            <img
              src={shot.url}
              alt={`New photo ${i + 1}`}
              className="h-16 w-20 object-cover rounded-xl border-2 border-black"
            />
            <button
              type="button"
              aria-label={`Remove new photo ${i + 1}`}
              className="absolute top-1 right-1 h-5 w-5 rounded-full bg-black/70 text-white text-[10px] leading-none flex items-center justify-center"
              onClick={() => {
                removeShot(i);
                if (shots.length === 1)
                  setTileCamera(false),
                    setStep(checked.length ? 'results' : 'camera');
              }}
            >
              ✕
            </button>
          </div>
        ))}
      {(step === 'ready' || step === 'results') &&
        room > 0 &&
        (tileCamera ? null : (
          <button
            type="button"
            onClick={() => {
              setError('');
              setTileCamera(true);
            }}
            disabled={applying}
            className="shrink-0 h-[68px] w-24 rounded-xl border-2 border-dashed border-black/20 bg-white text-black/80 hover:bg-black/5 disabled:opacity-40 flex flex-col items-center justify-center gap-1"
          >
            <CameraIcon />
            <span className="text-xs font-medium">Take photo</span>
          </button>
        ))}
    </div>
  );

  // One task on the results screen: unsure (Done / Not done), ticked (Undo) or not done (Mark done)
  const resultRow = (task: RunChecklistTree) => {
    const taskId = task.checklist.task_id as string;
    const r = results.find(x => x.task_id === taskId);
    if (!r || outOfPhoto.includes(taskId)) return null;
    const decision = decisions[taskId];
    const title = task.checklist.title || '';
    const sure =
      r.score == null
        ? ''
        : `${Math.round(Math.max(r.score, 1 - r.score) * 100)}% sure`;
    const photoLink = checked.length > 1 && decidedBy[taskId] !== undefined && (
      <>
        {' · '}
        <button
          type="button"
          className="underline underline-offset-2"
          onClick={() => setShown(decidedBy[taskId])}
        >
          Photo {decidedBy[taskId] + 1}
        </button>
      </>
    );
    const icon =
      decision === 'done' ? (
        <span className="shrink-0 h-6 w-6 rounded-md bg-black text-white flex items-center justify-center">
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="3"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M5 12l5 5L19 7" />
          </svg>
        </span>
      ) : decision === 'not_done' ? (
        <span className="shrink-0 h-6 w-6 rounded-md bg-red-100 text-red-700 text-sm font-bold flex items-center justify-center">
          !
        </span>
      ) : decision === null ? (
        <span className="shrink-0 h-6 w-6 rounded-md bg-yellow-200 text-sm font-bold flex items-center justify-center">
          ?
        </span>
      ) : (
        <span className="shrink-0 h-6 w-6 rounded-md border-2 border-black/10 border-t-black/50 animate-spin" />
      );
    const sub =
      decision === 'done'
        ? `Done${sure ? ' · ' + sure : ''}`
        : decision === 'not_done'
        ? `${notDoneLabel(r.problem, title).label}${sure ? ' · ' + sure : ''}`
        : decision === null
        ? `AI isn’t sure${
            r.score != null ? ' · ' + Math.round(r.score * 100) + '%' : ''
          }`
        : 'Ticking…';
    return (
      <div
        key={taskId}
        className="rounded-xl border border-black/10 px-4 py-3 flex flex-col gap-2"
      >
        <div className="flex items-center gap-3">
          {icon}
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium">{title}</div>
            <div className="text-xs text-black/60">
              {sub}
              {photoLink}
            </div>
          </div>
          {decision === null && !blocked.includes(taskId) && (
            <div className="shrink-0 flex gap-2">
              <button
                type="button"
                className="px-3 py-1.5 rounded-full border border-gray-700 bg-[#ffa726] text-xs font-medium disabled:opacity-40"
                onClick={() => decide(taskId, 'done')}
                disabled={applying}
              >
                Done
              </button>
              <button
                type="button"
                className="px-3 py-1.5 rounded-full border border-gray-700 bg-white text-xs font-medium disabled:opacity-40"
                onClick={() => decide(taskId, 'not_done')}
                disabled={applying}
              >
                Not done
              </button>
            </div>
          )}
          {decision === 'done' && (
            <button
              type="button"
              className={linkButton + ' shrink-0'}
              onClick={() => undoDone(taskId)}
              disabled={applying}
            >
              Undo
            </button>
          )}
          {decision === 'not_done' && (
            <button
              type="button"
              className={linkButton + ' shrink-0'}
              onClick={() => decide(taskId, 'done')}
              disabled={applying}
            >
              Mark done
            </button>
          )}
        </div>
        {blocked.includes(taskId) && (
          <p className="text-xs text-yellow-800">
            Not changed: this task isn&apos;t assigned to you, or waits for an
            earlier step.
          </p>
        )}
      </div>
    );
  };

  // Unsure first, then not done, then ticked
  const order = (taskId: string) =>
    decisions[taskId] === null ? 0 : decisions[taskId] === 'not_done' ? 1 : 2;
  const resultChecks = [...checks].sort(
    (a, b) =>
      order(a.task.checklist.task_id as string) -
      order(b.task.checklist.task_id as string)
  );

  const summaryTitle = applying
    ? 'Updating tasks…'
    : `${ticked} task${ticked === 1 ? '' : 's'} ticked${
        toCheck ? `, ${toCheck} to check` : ''
      }`;
  const summaryText = toCheck
    ? `Confirm the ${toCheck === 1 ? 'one' : toCheck} AI isn’t sure about.`
    : results.length
    ? 'AI ticked what it could see. You can undo any of them.'
    : 'None of the tasks were in the photos. Take another photo to cover them.';

  const errorBox = error && (
    <div className="rounded-xl bg-red-50 text-red-800 text-sm p-3">{error}</div>
  );

  return (
    <Transition appear show={true} as={Fragment}>
      <Dialog
        as="div"
        className="relative z-50"
        onClose={() => (step === 'working' || applying ? undefined : onClose())}
      >
        <div className="fixed inset-0 bg-black/30" />
        <div className="fixed inset-0 overflow-y-auto">
          <div
            className={
              'flex min-h-full justify-center p-2 sm:p-4 ' +
              // The "Checking photo…" card sits in the middle of the screen on phones too
              (step === 'working'
                ? 'items-center'
                : 'items-start sm:items-center')
            }
          >
            <Dialog.Panel className="w-full max-w-5xl rounded-2xl bg-white shadow-xl">
              {/* Header */}
              <div className="flex items-center justify-between gap-2 px-4 sm:px-6 py-4 border-b border-black/10">
                <div className="flex items-center gap-3">
                  <div className="shrink-0 h-11 w-11 rounded-xl bg-[#fff1d6] flex items-center justify-center">
                    <CameraIcon size={22} />
                  </div>
                  <div>
                    <Dialog.Title className="text-lg font-semibold leading-tight">
                      Check by photo
                    </Dialog.Title>
                    <p className="text-sm text-black/50">
                      {section.checklist?.title}
                    </p>
                  </div>
                </div>
                <PhotoCloseButton
                  onClick={onClose}
                  disabled={step === 'working' || applying}
                />
              </div>

              {/* Body */}
              {step === 'working' ? (
                <div className="px-6 py-16 flex flex-col items-center gap-3 text-sm text-black/70">
                  <span className="inline-block h-8 w-8 rounded-full border-4 border-black/10 border-t-black/60 animate-spin" />
                  {progress}
                </div>
              ) : (
                <div className="px-4 sm:px-6 py-5 grid md:grid-cols-2 gap-6 md:gap-8">
                  {/* Left: camera, or the photos */}
                  <div className="flex flex-col gap-3 min-w-0">
                    {step === 'camera' ? (
                      <RunPhotoCheckCamera
                        onCapture={addShot}
                        withLocation={true}
                        actions={(capture, ready) => (
                          <button
                            type="button"
                            className={
                              primaryButton +
                              ' flex items-center justify-center gap-2'
                            }
                            id={TAKE_PHOTO_BUTTON}
                            onClick={capture}
                            disabled={!ready || room <= 0}
                          >
                            <CameraIcon />
                            Take photo
                          </button>
                        )}
                      />
                    ) : (
                      <>
                        {tileCamera ? (
                          <RunPhotoCheckCamera
                            onCapture={addShot}
                            withLocation={true}
                            actions={(capture, ready) => (
                              <button
                                type="button"
                                className={
                                  primaryButton +
                                  ' flex items-center justify-center gap-2'
                                }
                                id={TAKE_PHOTO_BUTTON}
                                onClick={capture}
                                disabled={!ready || room <= 0}
                              >
                                <CameraIcon />
                                Take photo
                              </button>
                            )}
                          />
                        ) : (
                          <>
                            {bigUrl && (
                              <div className="relative w-full">
                                <img
                                  src={bigUrl}
                                  alt="Photo"
                                  className="block w-full rounded-2xl max-h-[50vh] object-contain bg-black/5"
                                />
                                {!pending &&
                                  results
                                    .filter(r => decidedBy[r.task_id] === shown)
                                    .map(r =>
                                      r.box ? (
                                        <div
                                          key={r.task_id}
                                          className="absolute border-2 border-red-600 rounded-sm pointer-events-none"
                                          style={{
                                            top: `${r.box.top * 100}%`,
                                            left: `${r.box.left * 100}%`,
                                            width: `${
                                              (r.box.right - r.box.left) * 100
                                            }%`,
                                            height: `${
                                              (r.box.bottom - r.box.top) * 100
                                            }%`
                                          }}
                                        />
                                      ) : null
                                    )}
                              </div>
                            )}
                            {photoMeta}
                          </>
                        )}
                        {thumbnails}
                      </>
                    )}
                  </div>

                  {/* Right: how it works, or what the photos ticked */}
                  <div className="flex flex-col gap-3 min-w-0">
                    {step === 'camera' && howItWorks('Take a photo')}
                    {step === 'ready' && (
                      <>
                        {howItWorks('Ready to check')}
                        {errorBox}
                      </>
                    )}
                    {step === 'results' && (
                      <>
                        <div>
                          <h3 className="text-xl font-semibold">
                            {summaryTitle}
                          </h3>
                          <p className="text-sm text-black/60 mt-1">
                            {summaryText}
                          </p>
                        </div>
                        {errorBox}
                        <div className="flex flex-col gap-3 overflow-y-auto max-h-[50vh] pr-1">
                          {resultChecks.map(({ task }) => resultRow(task))}
                          {outOfPhoto.length > 0 && (
                            <div className="rounded-xl border border-black/10 bg-black/[0.03] px-4 py-3">
                              <button
                                type="button"
                                className="w-full flex items-center gap-3 text-left"
                                onClick={() => setShowMissing(v => !v)}
                                aria-expanded={showMissing}
                              >
                                <span className="flex-1">
                                  <span className="block text-sm font-medium">
                                    {outOfPhoto.length} task
                                    {outOfPhoto.length === 1 ? '' : 's'} not in{' '}
                                    {checked.length > 1
                                      ? 'any photo'
                                      : 'the photo'}
                                  </span>
                                  <span className="block text-xs text-black/60">
                                    Left as{' '}
                                    {outOfPhoto.length === 1
                                      ? 'it is'
                                      : 'they are'}
                                    . Take another photo to cover{' '}
                                    {outOfPhoto.length === 1 ? 'it' : 'them'}.
                                  </span>
                                </span>
                                <svg
                                  className={
                                    'shrink-0 transition-transform ' +
                                    (showMissing ? 'rotate-180' : '')
                                  }
                                  width="18"
                                  height="18"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  aria-hidden="true"
                                >
                                  <path d="M6 9l6 6 6-6" />
                                </svg>
                              </button>
                              {showMissing && (
                                <ul className="mt-2 ml-4 list-disc text-xs text-black/70 flex flex-col gap-0.5">
                                  {checks
                                    .filter(({ task }) =>
                                      outOfPhoto.includes(
                                        task.checklist.task_id as string
                                      )
                                    )
                                    .map(({ task }) => (
                                      <li key={task.checklist.task_id}>
                                        {task.checklist.title}
                                      </li>
                                    ))}
                                </ul>
                              )}
                            </div>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                </div>
              )}

              {/* Footer */}
              {step !== 'working' && (
                <div className="flex flex-wrap items-center gap-3 px-4 sm:px-6 py-4 border-t border-black/10">
                  {confirmStartOver ? (
                    <>
                      <span className="flex-1 text-sm font-medium">
                        Remove all photos and undo what AI ticked?
                      </span>
                      <button
                        type="button"
                        className={secondaryButton}
                        onClick={() => setConfirmStartOver(false)}
                      >
                        Keep
                      </button>
                      <button
                        type="button"
                        className={
                          pillButton + ' bg-black text-white hover:bg-black/80'
                        }
                        onClick={startOver}
                        disabled={applying}
                      >
                        Yes, start over
                      </button>
                    </>
                  ) : (
                    <>
                      {(checked.length > 0 ||
                        (step === 'ready' && shots.length > 0)) && (
                        <button
                          type="button"
                          className="flex items-center gap-2 text-sm font-medium disabled:opacity-40"
                          onClick={() =>
                            checked.length
                              ? setConfirmStartOver(true)
                              : (discardShots(), setStep('camera'))
                          }
                          disabled={applying}
                        >
                          <svg
                            width="18"
                            height="18"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            aria-hidden="true"
                          >
                            <path d="M3 12a9 9 0 1 0 3-6.7L3 8" />
                            <path d="M3 3v5h5" />
                          </svg>
                          Start over
                        </button>
                      )}
                      <span className="flex-1" />
                      {step === 'camera' && (
                        <button
                          type="button"
                          className={secondaryButton}
                          onClick={cancelCamera}
                        >
                          Cancel
                        </button>
                      )}
                      {step === 'ready' && (
                        <>
                          <button
                            type="button"
                            className={secondaryButton}
                            onClick={cancelReady}
                          >
                            Cancel
                          </button>
                          <button
                            type="button"
                            className={primaryButton}
                            onClick={checkPhotos}
                            disabled={!shots.length}
                          >
                            Check {shots.length} photo
                            {shots.length === 1 ? '' : 's'}
                          </button>
                        </>
                      )}
                      {step === 'results' && (
                        <>
                          {room > 0 && (
                            <button
                              type="button"
                              className={
                                secondaryButton + ' flex items-center gap-2'
                              }
                              onClick={() => {
                                setError('');
                                setTileCamera(true);
                              }}
                              disabled={applying}
                            >
                              <CameraIcon />
                              Take another photo
                            </button>
                          )}
                          <button
                            type="button"
                            className={primaryButton}
                            onClick={onClose}
                            disabled={applying}
                          >
                            Done
                          </button>
                        </>
                      )}
                    </>
                  )}
                </div>
              )}
            </Dialog.Panel>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
